package com.arena_web.ui.sections.profile.mobile

import androidx.compose.runtime.*
import com.arena_web.data.dto.CreateUserPostRequest
import com.arena_web.data.dto.UserPostDto
import com.arena_web.domain.repositories.UserPostRepository
import com.arena_web.ui.components.feed.SlotImage
import com.arena_web.ui.components.feed.createObjectUrl
import com.arena_web.ui.components.feed.pickImageFile
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.localization.uploadErrorMessage
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.TextArea

// 50 MB matches the backend cap (mirrors CreatePostPopup.MAX_POST_BYTES).
private const val MAX_POST_BYTES = 50 * 1024 * 1024

/** Number of photo slots each layout requires (Figma 8664:434161 — six layouts). */
private fun slotsFor(layout: Int): Int = when (layout) {
    1, 2 -> 1
    3, 4 -> 2
    5, 6 -> 3
    else -> 1
}

/**
 * Mobile "Create Post" bottom sheet — Figma 8664:434161 (+ the 8230 / 8664
 * sibling frames showing each of the six selected layouts). The phone
 * counterpart to the desktop [com.arena_web.ui.components.feed.CreatePostPopup]:
 * identical pick → upload → createPost flow and the same six layout templates,
 * re-laid-out as a bottom sheet (drag handle / swipe-to-dismiss / scrim come
 * from the shared [BottomSheet]).
 *
 * The sheet drives the backend `layout` field (1–6); the selected layout decides
 * both the highlighted thumbnail and the arrangement of "Add Photo" drop slots
 * in the preview area. Lives behind the sheet's `--scale: var(--mobile-scale)`
 * wrapper, so the desktop `s()` helpers render at the 390-px mobile baseline.
 */
@Composable
fun MobileCreatePostSheet(
    postRepository: UserPostRepository,
    onDismiss: () -> Unit,
    onCreated: (UserPostDto) -> Unit,
) {
    val t = LocalAppStrings.current
    var layout by remember { mutableStateOf(1) }
    // Up to three slots (layouts 5/6 need three); trimmed when a smaller layout
    // is picked so a stale image never rides along into the request.
    var slotImages by remember { mutableStateOf<List<SlotImage?>>(List(3) { null }) }
    var caption by remember { mutableStateOf("") }
    var city by remember { mutableStateOf("") }
    var submitting by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    fun setSlot(index: Int, value: SlotImage?) {
        slotImages = slotImages.mapIndexed { i, prev -> if (i == index) value else prev }
    }

    val submit: () -> Unit = submit@{
        val required = slotsFor(layout)
        val provided = slotImages.take(required).filterNotNull()
        if (provided.size < required) {
            error = t.profileCreatePostAddAllPhotos(required)
            return@submit
        }
        submitting = true
        error = null
        scope.launch {
            val uploads = try {
                coroutineScope {
                    provided.map { slot ->
                        async { postRepository.uploadImage(slot.bytes, slot.fileName, slot.contentType) }
                    }.awaitAll()
                }
            } catch (@Suppress("TooGenericExceptionCaught") e: Throwable) {
                error = uploadErrorMessage(e, t, t.profileCreatePostUploadFailed)
                submitting = false
                return@launch
            }
            val urls = uploads.mapNotNull { it.getOrNull()?.imageUrl }
            if (urls.size != provided.size) {
                val cause = uploads.firstNotNullOfOrNull { it.exceptionOrNull() }
                error = uploadErrorMessage(cause, t, t.profileCreatePostUploadFailed)
                submitting = false
                return@launch
            }
            val create = postRepository.createPost(
                CreateUserPostRequest(
                    content = caption.trim(),
                    imageUrls = urls,
                    layout = layout,
                    city = city.trim().takeIf { it.isNotBlank() },
                )
            )
            val post = create.getOrNull()
            if (create.isFailure || post == null) {
                error = uploadErrorMessage(create.exceptionOrNull(), t, t.profileCreatePostCreateFailed)
                submitting = false
                return@launch
            }
            onCreated(post)
            onDismiss()
        }
    }

    // Full-height sheet (the six layouts + form overflow a phone), so the body
    // scrolls inside the panel while the drag handle stays pinned on top.
    BottomSheet(onDismiss = onDismiss, contentGap = 22, fullHeight = true) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sGap(22)
                .styleModifier {
                    property("flex", "1")
                    property("min-height", "0")
                    property("overflow-y", "auto")
                    property("scrollbar-width", "none")
                }
        ) {
            // ── Header: "Create Post" + faint divider (Figma 8664:437615) ──
            Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
                SpanText(
                    t.profileCreatePostTitle,
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(26)
                        .sLetterSpacing(0.2)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sHeight(1)
                        .styleModifier {
                            property(
                                "background",
                                "linear-gradient(90deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)"
                            )
                        }
                )
            }

            // ── Choose a Layout (label + 2×3 thumbnail grid) ──
            Column(modifier = Modifier.fillMaxWidth().sGap(24)) {
                SpanText(
                    t.profileCreatePostChooseLayout,
                    modifier = Modifier
                        .sFontSize(18)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(22)
                        .sLetterSpacing(0.09)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
                Column(modifier = Modifier.fillMaxWidth().sGap(24)) {
                    val pick: (Int) -> Unit = { id ->
                        if (layout != id) {
                            layout = id
                            slotImages = trim(slotImages, id)
                        }
                    }
                    // Row 1 — tiles 1-3, left-aligned with a 22-px gap (Figma 8664:437625).
                    Row(
                        modifier = Modifier.fillMaxWidth().sGap(22),
                        verticalAlignment = Alignment.Top
                    ) {
                        listOf(1, 2, 3).forEach { id -> LayoutTile(id, layout == id) { pick(id) } }
                    }
                    // Row 2 — tiles 4-6, justified across the width (Figma 8664:437652).
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.Top
                    ) {
                        listOf(4, 5, 6).forEach { id -> LayoutTile(id, layout == id) { pick(id) } }
                    }
                }
            }

            // ── Selected-layout preview: solid frame around the dashed drop
            //    zones for the active layout (Figma 8664:437694). ──
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sBorderRadius(14)
                    .styleModifier {
                        property("box-sizing", "border-box")
                        property("padding", s(12))
                        property("border", "0.5px solid rgba(60, 121, 241, 0.45)")
                        property(
                            "background",
                            "linear-gradient(189deg, rgba(60, 121, 241, 0.135) 38%, rgba(5, 4, 12, 0.03) 62%)"
                        )
                    }
            ) {
                SelectedLayoutZones(
                    layout = layout,
                    slots = slotImages,
                    onPick = { index ->
                        pickImageFile(MAX_POST_BYTES, onTooLarge = { error = t.profileCreatePostFileTooLarge }) { bytes, name, mime ->
                            setSlot(index, SlotImage(bytes, name, mime, createObjectUrl(bytes, mime)))
                            error = null
                        }
                    },
                    onClear = { index -> setSlot(index, null) },
                )
            }

            // ── Form fields + actions (Figma 8664:437704) ──
            Column(modifier = Modifier.fillMaxWidth().sGap(28)) {
                Column(modifier = Modifier.fillMaxWidth().sGap(22)) {
                    PostContentField(value = caption, onValueChange = { caption = it })
                    PostCityField(value = city, onValueChange = { city = it })
                }

                if (error != null) {
                    SpanText(error!!, modifier = Modifier.sFontSize(12).color(AppColors.Error))
                }

                Row(
                    modifier = Modifier.fillMaxWidth().sGap(12),
                    horizontalArrangement = Arrangement.End,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    ActionButton(text = t.commonCancel, primary = false, enabled = !submitting, onClick = onDismiss)
                    ActionButton(
                        text = if (submitting) t.profileCreatePostCreating else t.profileCreatePostTitle,
                        primary = true,
                        enabled = !submitting,
                        onClick = submit,
                    )
                }
            }

            // Trailing spacer — a sized Div at scroll-end keeps a comfortable
            // bottom gap (padding-bottom on a flex scroll container is dropped
            // at scroll-end on Chrome/Safari).
            Box(modifier = Modifier.fillMaxWidth().sHeight(8))
        }
    }
}

/** Drop any slot beyond the new layout's required count. */
private fun trim(slots: List<SlotImage?>, layout: Int): List<SlotImage?> =
    slots.mapIndexed { i, prev -> if (i >= slotsFor(layout)) null else prev }

// =============================================================================
// Layout selector tile — number circle on top, mini layout preview below.
// Portrait tiles (1, 3) are taller-than-wide; landscape tiles (2, 4, 5, 6) are
// wider-than-tall. Tile 2 (the single big landscape) is the largest.
// =============================================================================
@Composable
private fun LayoutTile(index: Int, active: Boolean, onClick: () -> Unit) {
    val isPortrait = index == 1 || index == 3
    val outerW = when {
        isPortrait -> 86
        index == 2 -> 116
        else -> 104
    }
    val outerH = when {
        isPortrait -> 115
        index == 2 -> 87
        else -> 78
    }
    Column(
        modifier = Modifier
            .sGap(22)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Number circle — 32×32, InteractiveSecondary fill + gradient hairline
        // (ButtonBorderGradientPrimary, drawn as a mask-composite ring).
        Box(
            modifier = Modifier
                .sSize(32)
                .sBorderRadius(100)
                .styleModifier {
                    property("position", "relative")
                    property("background", AppGradients.InteractiveSecondary)
                    property("flex-shrink", "0")
                },
            contentAlignment = Alignment.Center
        ) {
            GradientBorderOverlay(100)
            SpanText(
                "$index",
                modifier = Modifier
                    .sFontSize(18)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(22)
                    .sLetterSpacing(0.09)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
            )
        }
        // Preview frame — border highlights blue (#0c42ff) when active.
        Box(
            modifier = Modifier
                .sWidth(outerW)
                .sHeight(outerH)
                .sBorderRadius(6)
                .styleModifier {
                    property("box-sizing", "border-box")
                    property("flex-shrink", "0")
                    val border =
                        if (active) "0.5px solid #0c42ff"
                        else "0.3px solid rgba(255, 255, 255, 0.4)"
                    property("border", border)
                },
            contentAlignment = Alignment.Center
        ) {
            Box(
                modifier = Modifier.sWidth(outerW - 28).sHeight(outerH - 28),
                contentAlignment = Alignment.Center
            ) {
                LayoutMiniPreview(index, active)
            }
        }
    }
}

@Composable
private fun LayoutMiniPreview(index: Int, active: Boolean) {
    val big = 14
    val small = 8
    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        when (index) {
            1 -> MiniCell(active, big, Modifier.fillMaxSize())
            2 -> MiniCell(active, big, Modifier.fillMaxSize())
            3 -> Row(modifier = Modifier.fillMaxSize().sGap(3)) {
                MiniCell(active, small, Modifier.flexGrow(1).fillMaxHeight())
                MiniCell(active, small, Modifier.flexGrow(1).fillMaxHeight())
            }
            4 -> Column(modifier = Modifier.fillMaxSize().sGap(3)) {
                MiniCell(active, small, Modifier.fillMaxWidth().flexGrow(1))
                MiniCell(active, small, Modifier.fillMaxWidth().flexGrow(1))
            }
            5 -> Column(modifier = Modifier.fillMaxSize().sGap(3)) {
                MiniCell(active, small, Modifier.fillMaxWidth().flexGrow(1))
                Row(modifier = Modifier.fillMaxWidth().flexGrow(1).sGap(3)) {
                    MiniCell(active, small, Modifier.flexGrow(1).fillMaxHeight())
                    MiniCell(active, small, Modifier.flexGrow(1).fillMaxHeight())
                }
            }
            6 -> Column(modifier = Modifier.fillMaxSize().sGap(3)) {
                Row(modifier = Modifier.fillMaxWidth().flexGrow(1).sGap(3)) {
                    MiniCell(active, small, Modifier.flexGrow(1).fillMaxHeight())
                    MiniCell(active, small, Modifier.flexGrow(1).fillMaxHeight())
                }
                MiniCell(active, small, Modifier.fillMaxWidth().flexGrow(1))
            }
        }
    }
}

@Composable
private fun MiniCell(active: Boolean, iconSize: Int, modifier: Modifier = Modifier) {
    // Active cell uses the brighter Blue/200 (#004390); idle uses Blue/300 (#0a2e67).
    val cellBg = if (active) "#004390" else "#0a2e67"
    Box(
        modifier = modifier
            .sBorderRadius(4)
            .styleModifier {
                property("background", cellBg)
                property("border", "0.2px solid rgba(148, 149, 152, 0.3)")
                property("box-sizing", "border-box")
            },
        contentAlignment = Alignment.Center
    ) {
        // Recolour the image-outline glyph to the exact Figma slot tint (#506892).
        Box(
            modifier = Modifier
                .sSize(iconSize)
                .styleModifier {
                    property("background-color", "#506892")
                    val mask = "url('${AppIcons.ImageOutline}') no-repeat center / contain"
                    property("mask", mask)
                    property("-webkit-mask", mask)
                }
        )
    }
}

// =============================================================================
// Selected-layout drop zones — the dashed "Add Photo" slots, arranged per the
// active layout (Figma 8664:437695 and the sibling frames).
// =============================================================================
@Composable
private fun SelectedLayoutZones(
    layout: Int,
    slots: List<SlotImage?>,
    onPick: (Int) -> Unit,
    onClear: (Int) -> Unit,
) {
    val gap = 8
    when (layout) {
        1 -> DropSlot(slots.getOrNull(0), Modifier.fillMaxWidth().sHeight(300), { onPick(0) }, { onClear(0) })
        2 -> DropSlot(slots.getOrNull(0), Modifier.fillMaxWidth().sHeight(169), { onPick(0) }, { onClear(0) })
        3 -> Row(modifier = Modifier.fillMaxWidth().sHeight(240).sGap(gap)) {
            val m = Modifier.flexGrow(1).fillMaxHeight().styleModifier { property("flex-basis", "0") }
            DropSlot(slots.getOrNull(0), m, { onPick(0) }, { onClear(0) })
            DropSlot(slots.getOrNull(1), m, { onPick(1) }, { onClear(1) })
        }
        4 -> Column(modifier = Modifier.fillMaxWidth().sHeight(240).sGap(gap)) {
            val m = Modifier.fillMaxWidth().flexGrow(1).styleModifier { property("flex-basis", "0") }
            DropSlot(slots.getOrNull(0), m, { onPick(0) }, { onClear(0) })
            DropSlot(slots.getOrNull(1), m, { onPick(1) }, { onClear(1) })
        }
        5 -> Column(modifier = Modifier.fillMaxWidth().sGap(gap)) {
            DropSlot(slots.getOrNull(0), Modifier.fillMaxWidth().sHeight(150), { onPick(0) }, { onClear(0) })
            Row(modifier = Modifier.fillMaxWidth().sHeight(104).sGap(gap)) {
                val m = Modifier.flexGrow(1).fillMaxHeight().styleModifier { property("flex-basis", "0") }
                DropSlot(slots.getOrNull(1), m, { onPick(1) }, { onClear(1) })
                DropSlot(slots.getOrNull(2), m, { onPick(2) }, { onClear(2) })
            }
        }
        6 -> Column(modifier = Modifier.fillMaxWidth().sGap(gap)) {
            Row(modifier = Modifier.fillMaxWidth().sHeight(104).sGap(gap)) {
                val m = Modifier.flexGrow(1).fillMaxHeight().styleModifier { property("flex-basis", "0") }
                DropSlot(slots.getOrNull(0), m, { onPick(0) }, { onClear(0) })
                DropSlot(slots.getOrNull(1), m, { onPick(1) }, { onClear(1) })
            }
            DropSlot(slots.getOrNull(2), Modifier.fillMaxWidth().sHeight(150), { onPick(2) }, { onClear(2) })
        }
    }
}

@Composable
private fun DropSlot(
    slot: SlotImage?,
    modifier: Modifier = Modifier,
    onPick: () -> Unit,
    onClear: () -> Unit,
) {
    val t = LocalAppStrings.current
    Box(
        modifier = modifier
            .sBorderRadius(14)
            .cursor(Cursor.Pointer)
            .onClick { if (slot == null) onPick() }
            .styleModifier {
                property("box-sizing", "border-box")
                property("position", "relative")
                property("overflow", "hidden")
                property("min-width", "0")
                property("min-height", "0")
                if (slot == null) {
                    property("border", "1px dashed #0c1787")
                    property(
                        "background",
                        "linear-gradient(200deg, rgba(60, 121, 241, 0.06) 49%, rgba(5, 4, 12, 0.06) 106%)"
                    )
                } else {
                    // The image is a CSS background so it can never grow the slot;
                    // `cover` crops to the layout-dictated dimensions.
                    property("border", "1px solid #0c1787")
                    property("background-image", "url('${slot.previewUrl}')")
                    property("background-size", "cover")
                    property("background-position", "center")
                    property("background-repeat", "no-repeat")
                }
            },
        contentAlignment = Alignment.Center
    ) {
        if (slot == null) {
            Column(modifier = Modifier.sGap(10), horizontalAlignment = Alignment.CenterHorizontally) {
                // "+" circle — InteractiveSecondary fill + gradient hairline
                // (ButtonBorderGradientPrimary ring), matching the Figma
                // "Expand Icon" thin-outline plus.
                Box(
                    modifier = Modifier
                        .sSize(32)
                        .sBorderRadius(100)
                        .styleModifier {
                            property("position", "relative")
                            property("background", AppGradients.InteractiveSecondary)
                        },
                    contentAlignment = Alignment.Center
                ) {
                    GradientBorderOverlay(100)
                    Icon(src = AppIcons.AddSecondary, modifier = Modifier.sSize(14))
                }
                SpanText(
                    t.profileCreatePostAddPhoto,
                    modifier = Modifier
                        .sFontSize(12)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(12)
                        .sLetterSpacing(0.012)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }
        } else {
            // Remove button (top-right).
            Box(
                modifier = Modifier
                    .sSize(28)
                    .sBorderRadius(100)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("position", "absolute")
                        property("top", s(8))
                        property("right", s(8))
                        property("background", "rgba(0, 0, 0, 0.6)")
                        property("border", "0.3px solid var(--arena-border-top)")
                    }
                    .onClick {
                        it.stopPropagation()
                        onClear()
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText("×", modifier = Modifier.sFontSize(16).color(com.arena_web.ui.theme.ThemeVars.Text2))
            }
        }
    }
}

// =============================================================================
// Form fields — Post Content (textarea) + City (single line w/ location icon).
// Both carry a translucent InteractiveSecondary fill and a gradient hairline
// drawn as a mask-composite ring so the input below stays interactive.
// =============================================================================
@Composable
private fun PostContentField(value: String, onValueChange: (String) -> Unit) {
    val t = LocalAppStrings.current
    Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
        FieldLabel(t.profileCreatePostContentLabel)
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(96)
                .styleModifier {
                    property("position", "relative")
                    property("background", AppGradients.InteractiveSecondary)
                    property("border-radius", s(18))
                }
        ) {
            GradientBorderOverlay(18)
            Box(
                modifier = Modifier.fillMaxSize().styleModifier {
                    property("padding", s(12))
                    property("box-sizing", "border-box")
                }
            ) {
                TextArea {
                    value(value)
                    onInput { onValueChange(it.value) }
                    attr("placeholder", t.profileCreatePostContentPlaceholder)
                    style {
                        width(100.percent)
                        property("height", "100%")
                        property("background", "transparent")
                        property("border", "none")
                        property("outline", "none")
                        property("font-size", s(16))
                        property("font-family", "Urbanist, sans-serif")
                        property("color", "var(--arena-text-2)")
                        property("padding", "0")
                        property("resize", "none")
                    }
                }
            }
        }
    }
}

@Composable
private fun PostCityField(value: String, onValueChange: (String) -> Unit) {
    val t = LocalAppStrings.current
    Column(modifier = Modifier.fillMaxWidth().sGap(6)) {
        FieldLabel(t.profileCreatePostCityLabel)
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(44)
                .styleModifier {
                    property("position", "relative")
                    property("background", AppGradients.InteractiveSecondary)
                    property("border-radius", s(28))
                }
        ) {
            GradientBorderOverlay(28)
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .sGap(12)
                    .styleModifier {
                        property("padding", "0 ${s(8)} 0 ${s(12)}")
                        property("box-sizing", "border-box")
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    src = AppIcons.Location,
                    modifier = Modifier.sSize(24).styleModifier { property("opacity", "0.7") }
                )
                Input(type = InputType.Text) {
                    value(value)
                    onInput { onValueChange(it.value) }
                    attr("placeholder", t.profileCreatePostCityPlaceholder)
                    style {
                        width(100.percent)
                        property("background", "transparent")
                        property("border", "none")
                        property("outline", "none")
                        property("font-size", s(16))
                        property("font-family", "Urbanist, sans-serif")
                        property("color", "var(--arena-text-2)")
                        property("padding", "0")
                    }
                }
            }
        }
    }
}

@Composable
private fun FieldLabel(label: String) {
    SpanText(
        label,
        modifier = Modifier
            .sPaddingLeft(4)
            .sFontSize(14)
            .fontWeight(AppTypography.Regular)
            .sLineHeight(22)
            .sLetterSpacing(0.028)
            .color(com.arena_web.ui.theme.ThemeVars.Text2)
    )
}

@Composable
private fun GradientBorderOverlay(borderRadius: Int) {
    Box(
        modifier = Modifier.fillMaxSize().styleModifier {
            property("position", "absolute")
            property("inset", "0")
            property("border", "0.3px solid transparent")
            property("border-radius", s(borderRadius))
            property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
            property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
            property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
            property("-webkit-mask-composite", "xor")
            property("mask-composite", "exclude")
            property("pointer-events", "none")
        }
    )
}

// =============================================================================
// Action buttons — equal halves filling the form width. Primary = solid sport
// fill; secondary "Cancel" = translucent InteractiveSecondary. Both carry the
// ButtonBorderGradientPrimary hairline. Mirrors MobileCreateStorySheet.
// =============================================================================
@Composable
private fun ActionButton(text: String, primary: Boolean, enabled: Boolean, onClick: () -> Unit) {
    val sportColors = LocalSportColors.current
    val label: @Composable () -> Unit = {
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.032)
                .color(if (primary) org.jetbrains.compose.web.css.Color(AppColors.PrimaryWhite.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
    if (primary) {
        val fill = sportColors.primary.toRgb()
        val fillCss = "rgb(${fill.red}, ${fill.green}, ${fill.blue})"
        Box(
            modifier = Modifier
                .flexGrow(1)
                .sHeight(44)
                .sBorderRadius(100)
                .cursor(if (enabled) Cursor.Pointer else Cursor.Default)
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "linear-gradient($fillCss, $fillCss) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                    property("opacity", if (enabled) "1" else "0.5")
                }
                .onClick { if (enabled) onClick() },
            contentAlignment = Alignment.Center
        ) { label() }
    } else {
        Box(
            modifier = Modifier
                .flexGrow(1)
                .sHeight(44)
                .sBorderRadius(100)
                .cursor(if (enabled) Cursor.Pointer else Cursor.Default)
                .styleModifier {
                    property("position", "relative")
                    property("background", AppGradients.InteractiveSecondary)
                    property("opacity", if (enabled) "1" else "0.5")
                }
                .onClick { if (enabled) onClick() },
            contentAlignment = Alignment.Center
        ) {
            GradientBorderOverlay(100)
            label()
        }
    }
}
